import {
  arrayBufferDownloadExcel,
} from "../../../../utils/download";
import {
  decorateEmptyGridValues,
} from "../../../../utils/table/gridUtils";
import {
  FETCH_REVENUE_BY_NETWORK_LIST,
  FETCH_REVENUE_BY_NETWORK_INIT,
  EXPORT_REVENUE_BY_NETWORK,
  TOAST,
  SET_REVENUE_BY_NETWORK_FILTERS,
  CLEAR_REVENUE_BY_NETWORK_FILTERS,
} from "../../../types/Reservations/RevenueByNetwork.type";
import { getCurrentAndThreeDaysLater } from "../../../../utils";

const getDefaultFilters = () => {
  const { currentDate, threeDaysLater } = getCurrentAndThreeDaysLater();
  return {
    startDate: currentDate,
    endDate: threeDaysLater,
    location: "",
    network: "",
  };
};

const INITIAL_STATE = {
  RevenueByNetworkListview: [],
  isLoading: false,
  error: null,
  isExportSuccess: false,
  isExportError: false,
  filters: getDefaultFilters(),
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "-";
  return `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const transformToGridView = (data) => {
  const rows = [];

  if (data?.report) {
    data.report.forEach((location) => {
      location.networks.forEach((network) => {
        const transformedValues = {
          locationName: location.location_name,
          networkName: network.network_name,
          confirmedCount: network.confirmed_count,
          cancelledCount: network.cancelled_count,
          noShowCount: network.no_show_count,
          totalCount: network.total_count,
          confirmedRevenue: formatCurrency(network.confirmed_revenue),
          cancelledRevenue: formatCurrency(network.cancelled_revenue),
          noShowRevenue: formatCurrency(network.no_show_revenue),
          totalRevenue: formatCurrency(network.total_revenue),
        };
        const decoratedValues = {};
        Object.keys(transformedValues).forEach((key) => {
          decoratedValues[key] = decorateEmptyGridValues(transformedValues[key]);
        });
        rows.push(decoratedValues);
      });
    });
  }

  const columns = [
    {
      colLabel: "Location",
      dataKey: "locationName",
      extraProps: {
        isSortable: false,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
    {
      colLabel: "Network",
      dataKey: "networkName",
      extraProps: {
        isSortable: false,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
    {
      colLabel: "Confirmed Count",
      dataKey: "confirmedCount",
      extraProps: {
        isSortable: false,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
    {
      colLabel: "Cancelled Count",
      dataKey: "cancelledCount",
      extraProps: {
        isSortable: false,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
    {
      colLabel: "No Show Count",
      dataKey: "noShowCount",
      extraProps: {
        isSortable: false,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
    {
      colLabel: "Total Count",
      dataKey: "totalCount",
      extraProps: {
        isSortable: false,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
    {
      colLabel: "Confirmed Revenue",
      dataKey: "confirmedRevenue",
      extraProps: {
        isSortable: false,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
    {
      colLabel: "Cancelled Revenue",
      dataKey: "cancelledRevenue",
      extraProps: {
        isSortable: false,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
    {
      colLabel: "No Show Revenue",
      dataKey: "noShowRevenue",
      extraProps: {
        isSortable: false,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
    {
      colLabel: "Total Revenue",
      dataKey: "totalRevenue",
      extraProps: {
        isSortable: false,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
  ];

  return { columns, rows };
};

export const RevenueByNetworkReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case FETCH_REVENUE_BY_NETWORK_INIT:
      return { ...INITIAL_STATE, isLoadingPage: false };

    case FETCH_REVENUE_BY_NETWORK_LIST.START:
      return { ...state, isLoading: true };

    case FETCH_REVENUE_BY_NETWORK_LIST.SUCCESS: {
      const data = action.payload;

      if (!data?.report) {
        return {
          ...state,
          RevenueByNetworkListview: [],
          paginationInfo: null,
          isLoading: false,
          isLoadingPage: false,
        };
      }

      const { columns, rows } = transformToGridView(data);

      return {
        ...state,
        RevenueByNetworkListview: { columns, rows },
        paginationInfo: {
          pageSize: data.page_size,
          totalRecords: data.total_locations,
          totalPages: Math.ceil(data.total_locations / data.page_size),
          pageNumber: data.page,
        },
        totals: data.totals,
        isLoading: false,
        isLoadingPage: false,
        error: null,
      };
    }

    case FETCH_REVENUE_BY_NETWORK_LIST.ERROR: {
      return {
        ...state,
        RevenueByNetworkListview: [],
        isLoading: false,
        isLoadingPage: false,
        error: action.payload?.error,
        isPageLevelError: true,
      };
    }

    case EXPORT_REVENUE_BY_NETWORK.START:
      return { ...state, isLoading: true };

    case EXPORT_REVENUE_BY_NETWORK.SUCCESS: {
      arrayBufferDownloadExcel(action.payload, "revenue-by-network.xlsx");
      return {
        ...state,
        isLoading: false,
        isExportSuccess: true,
        isExportError: false,
      };
    }

    case EXPORT_REVENUE_BY_NETWORK.ERROR: {
      return {
        ...state,
        isLoading: false,
        isExportSuccess: false,
        isExportError: true,
      };
    }

    case TOAST.RESET: {
      return {
        ...state,
        isExportSuccess: false,
        isExportError: false,
      };
    }

    case SET_REVENUE_BY_NETWORK_FILTERS: {
      return {
        ...state,
        filters: action.payload,
      };
    }

    case CLEAR_REVENUE_BY_NETWORK_FILTERS: {
      return {
        ...state,
        filters: getDefaultFilters(),
      };
    }

    default:
      return state;
  }
};
